import { PAGE_SIZE_OPTIONS, usePageParams } from '../hooks/use-page-params';
import type { SubscriptionStatusFilter, SubscriptionType } from '../types/types';
import { EditSubscriptionEndDateDialog } from '@/components/Subscriptions/edit-subscription-end-date-dialog';
import {
  PRODUCT_FILTER_VALUES,
  type ProductFilter,
  STATUS_FILTER_VALUES,
  type StatusFilter,
  SubscriptionsFilterBar
} from '@/components/Subscriptions/subscriptions-filter-bar';
import { useDebouncedFilterParam } from '@/hooks/use-debounced-filter-param';
import { useSortParam } from '@/hooks/use-sort-param';
import { useSubscriptions, useSubscriptionsQuery } from '@/hooks/use-subscriptions';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import Table from 'components/Basic/Table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { type SubscriptionStatusLabel, getSubscriptionStatus } from 'utils/kpiCalculations';
import {
  type SortParam,
  parseEnumParam,
  parseStringParam,
  setFilterParams
} from 'utils/list-filter-params';
import { formatDate } from 'utils/utils';

type SubscriptionRow = {
  id: string;
  customerId: string;
  customerName: string;
  address: string;
  productType: SubscriptionType;
  price: number;
  endDate: string;
  status: SubscriptionStatusLabel;
  statusDays: number;
  version: number;
};

const STATUS_LABEL_TO_FILTER: Record<SubscriptionStatusLabel, SubscriptionStatusFilter> = {
  active: 'ACTIVE',
  ending_soon: 'ENDING_SOON',
  expired: 'EXPIRED'
};

const PRODUCT_CHIP_COLORS: Record<SubscriptionType, string> = {
  ELEGANCE: '#008080',
  E_MAX: '#1565c0',
  WATER_ONLY: '#7b1fa2',
  SHORT_RENTAL: '#e65100'
};

const PRODUCT_DISPLAY_LABELS: Record<SubscriptionType, string> = {
  E_MAX: 'E-MAX',
  ELEGANCE: 'ELEGANCE',
  WATER_ONLY: 'WATER ONLY',
  SHORT_RENTAL: 'SHORT-TERM'
};

// REQ-8: both filter keys, next to the page/size pair use-page-params.ts owns for this route; the
// allowed values come from subscriptions-filter-bar's own value tuples, so they are listed once.
const SEARCH_PARAM = 'subscriptionsSearch';
const PRODUCT_PARAM = 'subscriptionsProduct';
const STATUS_PARAM = 'subscriptionsStatus';
const PAGE_PARAM = 'subscriptionsPage';
// REQ-8 (owner decision 2026-10-24, SHARED_CONTRACTS round 2 "Sorting"): the exact field names
// `GET /api/v1/subscriptions` accepts — `status` (the derived active/ending-soon/expired badge)
// stays unsortable, since the API has no column for it.
const SORT_PARAM = 'subscriptionsSort';
const SUBSCRIPTIONS_SORT_FIELDS = ['customerName', 'productType', 'price', 'endDate'] as const;
const DEFAULT_SORT: SortParam<(typeof SUBSCRIPTIONS_SORT_FIELDS)[number]> = {
  field: 'endDate',
  sort: 'asc'
};

const COLUMNS: GridColDef<SubscriptionRow>[] = [
  {
    field: 'customerName',
    headerName: 'CUSTOMER',
    minWidth: 200,
    renderCell: params => params.row.customerName
  },
  {
    field: 'productType',
    headerName: 'PRODUCT',
    renderCell: params => (
      <Chip
        label={PRODUCT_DISPLAY_LABELS[params.value as SubscriptionType]}
        size="small"
        sx={{
          bgcolor: PRODUCT_CHIP_COLORS[params.value as SubscriptionType],
          color: '#fff',
          fontWeight: 700,
          fontSize: '0.68rem',
          height: 22,
          borderRadius: 1
        }}
      />
    )
  },
  {
    field: 'price',
    headerName: 'PRICE/YR',
    renderCell: params => (params.value ? `€${params.value}` : '—')
  },
  {
    field: 'endDate',
    headerName: 'END DATE',
    renderCell: params => formatDate(dayjs(params.value as string))
  },
  {
    field: 'status',
    headerName: 'STATUS',
    sortable: false,
    renderCell: params => {
      const row = params.row;
      if (row.status === 'expired') {
        return (
          <Typography variant="body2" fontWeight={600} color="error.main">
            {row.statusDays}d overdue
          </Typography>
        );
      }
      if (row.status === 'ending_soon') {
        return (
          <Typography variant="body2" fontWeight={600} sx={{ color: 'warning.main' }}>
            {row.statusDays === 0 ? 'Expires today' : `Expires in ${row.statusDays}d`}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" fontWeight={600} color="success.main">
          Active
        </Typography>
      );
    }
  }
];

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { setEndDate } = useSubscriptions();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize, setPageParams } = usePageParams('subscriptions');

  // REQ-8: every filter lives in the URL (react-base/RULES.md § State management), next to the
  // existing page number, so a reload or a shared link reopens the same list.
  const productFilter = parseEnumParam(searchParams, PRODUCT_PARAM, PRODUCT_FILTER_VALUES, 'ALL');
  const statusFilter = parseEnumParam(searchParams, STATUS_PARAM, STATUS_FILTER_VALUES, 'ALL');
  const appliedSearch = parseStringParam(searchParams, SEARCH_PARAM);
  // REQ-8: searched, filtered and paged on the server now (apps/api/README.md § API Conventions),
  // the same debounce-then-commit-to-the-URL shape every other list page uses
  // (use-debounced-filter-param.ts).
  const [searchValue, setSearchValue] = useDebouncedFilterParam(
    SEARCH_PARAM,
    PAGE_PARAM,
    appliedSearch
  );
  // REQ-9/REQ-10: the id only, re-derived against `rows` on every render — see OrdersPage's own
  // selectedRowId for why (a background reload after a 409 must be reflected immediately, so the
  // next save carries the fresh version).
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<string>();

  const handleProductFilterChange = (value: ProductFilter) => {
    setSearchParams(prev => setFilterParams(prev, { [PRODUCT_PARAM]: value }, PAGE_PARAM), {
      replace: true
    });
  };
  const handleStatusFilterChange = (value: StatusFilter) => {
    setSearchParams(prev => setFilterParams(prev, { [STATUS_PARAM]: value }, PAGE_PARAM), {
      replace: true
    });
  };
  // REQ-8 (owner decision 2026-10-24): column sorting happens on the server — the clicked column
  // and direction live in the URL next to the other filters, defaulting to the same `endDate,asc`
  // `SubscriptionController` itself defaults to when no `sort` reaches it.
  const { sortModel, sortParam, handleSortModelChange } = useSortParam(
    SORT_PARAM,
    PAGE_PARAM,
    SUBSCRIPTIONS_SORT_FIELDS,
    DEFAULT_SORT
  );

  const { data, isLoading, error, refetch } = useSubscriptionsQuery(page, pageSize, {
    ...(appliedSearch && { search: appliedSearch }),
    ...(productFilter !== 'ALL' && { type: productFilter }),
    ...(statusFilter !== 'ALL' && {
      status: STATUS_LABEL_TO_FILTER[statusFilter]
    }),
    sort: sortParam
  });

  const rows: SubscriptionRow[] = (data?.content ?? []).map(row => {
    const { status, days } = getSubscriptionStatus(row.endDate);
    return {
      id: row.id,
      customerId: row.customerId,
      customerName: row.customerName,
      address: `${row.street}, ${row.city}`,
      productType: row.type,
      price: row.price,
      endDate: row.endDate,
      status,
      statusDays: days,
      version: row.version
    };
  });
  const editingSubscription = rows.find(row => row.id === editingSubscriptionId);

  // REQ-10: an Actions column appended to the shared COLUMNS — kept out of that module-level
  // array because it closes over this component's own setEditingSubscriptionId and navigate, the
  // same shape DriversSection/TrucksSection use for their own row actions.
  const columns: GridColDef<SubscriptionRow>[] = [
    ...COLUMNS,
    {
      field: 'rowActions',
      headerName: 'ACTIONS',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <>
          <IconButton
            size="small"
            aria-label={`View customer ${params.row.customerName}`}
            onClick={() => navigate(`/customers/${params.row.customerId}`)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Edit end date for ${params.row.customerName}`}
            onClick={() => setEditingSubscriptionId(params.row.id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </>
      )
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      {/* Filters */}
      <SubscriptionsFilterBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        productFilter={productFilter}
        onProductFilterChange={handleProductFilterChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        totalElements={data?.totalElements ?? 0}
      />

      {/* Table */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Table
          rows={rows}
          columns={columns}
          loading={isLoading}
          error={error || undefined}
          onRetry={refetch}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={data?.totalElements ?? 0}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={model => setPageParams(model.page, model.pageSize)}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          sortingMode="server"
          sortModel={[sortModel]}
          onSortModelChange={handleSortModelChange}
        />
      </Box>

      {editingSubscription && (
        <EditSubscriptionEndDateDialog
          subscription={{
            id: editingSubscription.id,
            customerId: editingSubscription.customerId,
            customerName: editingSubscription.customerName,
            type: editingSubscription.productType,
            endDate: editingSubscription.endDate,
            version: editingSubscription.version
          }}
          setEndDate={setEndDate}
          onClose={() => setEditingSubscriptionId(undefined)}
        />
      )}
    </Box>
  );
};

export default SubscriptionsPage;
